import { Experience } from "./quiz/experience";
import { Gender } from "./quiz/gender";
import { Goals } from "./quiz/goals";
import { MuscleFocus } from "./quiz/muscleFocus";

import { PrecomputedEvaluationsInterface, StatsigClient } from "@statsig/js-client";
import { NumberWorkouts } from "./quiz/numberWorkouts";
import { Physiology } from "./quiz/physiology";
import { TimeAvailable } from "./quiz/timeAvailable";
import { getWeightPounds } from "./quiz/weight";
import { User } from "./types";
import { Age } from "./quiz/age";
import Cookies from "js-cookie";
import { getStoredTrackingParams } from "./trackingParams";

type Guard<T> = (value: unknown) => value is T;
const isString: Guard<string> = (value: unknown): value is string =>
  typeof value === "string";
const isNumber: Guard<number> = (value: unknown): value is number =>
  typeof value === "number";
const isBoolean: Guard<boolean> = (value: unknown): value is boolean =>
  typeof value === "boolean";
const isAny: Guard<any> = (value: unknown): value is any => true;

const isRecordOf =
  <A>(itemGuard: Guard<A>): Guard<Record<string, A>> =>
  (x: unknown): x is Record<string, A> =>
    typeof x === "object" && x !== null && Object.values(x).every(itemGuard);
const isArrayOf =
  <A>(itemGuard: Guard<A>): Guard<Array<A>> =>
  (x: unknown): x is A[] =>
    Array.isArray(x) && x.every(itemGuard);

type PropertyGuards<A extends object> = { [K in keyof A]: Guard<A[K]> };

const isType =
  <A extends object>(propertyGuards: PropertyGuards<A>): Guard<A> =>
  (x: unknown): x is A =>
    typeof x === "object" &&
    x !== null &&
    Object.entries<Guard<A[keyof A]>>(propertyGuards).every(([k, guard]) =>
      guard((x as A)[k as keyof A])
    );

export type { Guard };

  export {
    isAny,
    isArrayOf,
    isBoolean,
    isNumber,
    isRecordOf,
    isString,
    isType
  };

export const answerTrackerQuestionMap: Record<
  string,
  (answer: any, allAnswers: any) => Object | undefined
> = {
  goals: (answer, allAnswers) => {
    const map = {
      [Goals.LoseFat]: "Lose Fat",
      [Goals.BuildMuscle]: "Build Muscle",
      [Goals.BuildMuscleAndLoseFat]: "Lose Fat",
    };
    return {
      goal: map[answer as Goals],
    };
  },
  gender: (answer, allAnswers) => {
    if (answer === Gender.Female || answer === Gender.Male) {
      return {
        gender: answer.toLowerCase(),
      };
    }
    // non-binary will be handled after phisology is answered
    return undefined;
  },
  physiology: (answer, allAnswers) => {
    return {
      gender: answer === Physiology.Male ? "male" : "female",
    };
  },
  height: (answer, allAnswers) => {
    console.log("height", answer, allAnswers);
    if (allAnswers.heightUnit === "cm") {
      return {
        heightCm: allAnswers.heightRaw,
        heightUnit: allAnswers.heightUnit,
      };
    }
    const heightCm =
      allAnswers.heightRaw * 30.48 + allAnswers.heightInches * 2.54;
    return { heightCm };
  },
  weight: (answer, allAnswers) => {
    console.log("weight", answer, allAnswers);
    const lbs = getWeightPounds(answer, allAnswers.weightUnit);
    return { weightUnit: allAnswers.weightUnit, lbs };
  },
  name: (answer) => ({ name: answer }),
  bodyFat: (answer) => ({ bodyfat: answer, bodyFatMethod: "estimate" }),
  muscleGain: (answer) => ({ muscleGain: answer }),
  // Map to LEVEL and not experience for now.
  experience: (answer) => {
    const levelMap = {
      [Experience.Beginner]: "Beginner Phase 2",
      [Experience.Intermediate]: "Intermediate",
      [Experience.Advanced]: "Intermediate",
    };

    const experienceMap = {
      [Experience.Beginner]: "beginner",
      [Experience.Intermediate]: "intermediate",
      [Experience.Advanced]: "intermediate",
    };

    return {
      level: levelMap[answer as Experience],
      experienceChoice: experienceMap[answer as Experience],
    };
  },
  muscleFocus: (answer) => {
    if (answer.includes(MuscleFocus.Equally)) {
      // equally is actually same as empty array
      return { priority: [] };
    }
    return { priority: [answer.toLowerCase()] };
  },
  numberWorkouts: (answer) => {
    if (answer === NumberWorkouts.AsManyAsPossible) {
      return { split: 6 };
    }
    return { split: parseInt(answer) };
  },
  timeAvailable: (answer) => {
    if (answer === TimeAvailable.ThirtyMinutes) {
      return { durationChoice: 30, duration: "30-Minute" };
    }
    if (answer === TimeAvailable.SixtyMinutes) {
      return { durationChoice: 60, duration: "Regular" };
    }
    return undefined;
  },
};

export function getInternalCoreEndpoint() {
  return "https://core.builtwithscience.com";
}

/**
 * Allow reading of the assigned Statsig experiment/arm
 * + Allow forcing ane experiment/arm through local storage for testing purposes
 * @param experimentName
 * @param client
 */
export function getAssignedExperimentArm(
  experimentName: string,
  client: StatsigClient | PrecomputedEvaluationsInterface
) {
  const localStorageExperimentName = localStorage.getItem("forceExperiment"); // forceExperiment=web09
  const localStorageExperimentArm = localStorage.getItem("forceArm"); // forceArm=variant

  if (
    localStorageExperimentName &&
    localStorageExperimentArm &&
    experimentName === localStorageExperimentName
  ) {
    console.log(
      "Forcing experiment through local storage params {forceExperiment} and {forceArm}",
      localStorageExperimentName,
      localStorageExperimentArm
    );

    return localStorageExperimentArm;
  }

  const statsigExperiment = client?.getExperiment(experimentName);
  const statsigArm = statsigExperiment
    ? statsigExperiment?.value["arm"]
    : undefined;

  return statsigArm;
}

interface UTMParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string | null;
  utm_content: string;
  fbp: string | null;
  fbc: string | null;
}

// Cookie retrieval helper with URI decoding
export function getCookie(name: string) {
  const matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  if (matches) {
    return decodeURIComponent(matches[1]);
  }

  // Fallback: check persisted UTM params in localStorage
  try {
    const params = getStoredTrackingParams() as Record<
      string,
      string | null | undefined
    >;
    const value = params?.[name];
    if (value == null) return undefined;
    return value;
  } catch {
    return undefined;
  }
}

// Main UTM extraction function
export function getUTMParams(): UTMParams | null {
  try {
    return {
      utm_source: getCookie("utm_source") || "",
      utm_medium: getCookie("utm_medium") || "",
      utm_campaign: getCookie("utm_campaign") || "",
      utm_term: getCookie("utm_term") || null,
      utm_content: getCookie("utm_content") || "",
      fbp: getCookie("_fbp") || null,
      fbc: getCookie("_fbc") || null,
    };
  } catch (error) {
    return null;
  }
}

export function getLandingPageSurveyItems(user: User) {
  const isFemale = user.gender === Gender.Female || (user.gender === Gender.NonBinary && user.physiology === Physiology.Female);
  const isMale = user.gender === Gender.Male || (user.gender === Gender.NonBinary && user.physiology === Physiology.Male);
  const isAge30To39 = user.age === Age.Thirties;
  const isAge50Plus = user.age === Age.OverFifty;

  const isGoalLoseFatOrLoseWeight = user.goals === Goals.LoseFat || user.goals === Goals.BuildMuscleAndLoseFat;

  // Female users with Lose Fat or Lose Weight goals
  const isSegment1 = isFemale && isGoalLoseFatOrLoseWeight;

  // Male users aged 30–39 with Lose Fat or Lose Weight goals
  const isSegment2 = isMale && isAge30To39 && isGoalLoseFatOrLoseWeight;

  // Male users aged 50+ with Lose Fat or Lose Weight goals
  const isSegment3 = isMale && isAge50Plus && isGoalLoseFatOrLoseWeight;

  if (isSegment1) {
    return [
      {
        id: "1",
        title: "Understanding Your Body’s Starting Point",
        text: "Factoring in your energy levels, recovery patterns, and body changes...",
        duration: 5000,
      },
      {
        id: "2",
        title: "Supporting Fat Loss Through Balance",
        text: "Aligning your goals with hormonal balance, energy, and stress levels...",
        question: {
          id: "preworkout",
          text: "How is your energy usually when it’s time to work out?",
          answers: ["High, I usually feel ready", "It depends on the day", "Low, I often feel drained"],
        },
        duration: 3500,
      },
      {
        id: "3",
        title: "Adapting Workouts to Daily Energy",
        text: "Designing sessions that flex with how you feel day to day...",
        question: {
          id: "trainingTime",
          text: "When do you usually feel most capable of training?",
          answers: ["Morning", "Evening", "It varies"],
        },
        duration: 4000,
      },
      {
        id: "4",
        title: "Supporting Recovery, Metabolism & Natural Fluctuations",
        text: "Accounting for sleep, stress, and natural changes in energy across the month...",
        question: {
          id: "sleepHours",
          text: "About how many days per month do you feel lower energy, slower recovery, or need extra support?",
          answers: ["More than 7 days", "4 – 7 days", "1 – 3 days", "Rarely or never"],
        },
        duration: 5000,
      },
      {
        id: "5",
        title: "Fitting This Plan Into Real Life",
        text: "Adapting everything to your routine, stress, and recovery needs...",
        duration: 4000,
      },
    ];
  }

  if (isSegment2) {
    return [
      {
        id: "1",
        title: "Assessing Your Current Baseline",
        text: "Reviewing your fitness level alongside work and life demands...",
        duration: 5000,
      },
      {
        id: "2",
        title: "Setting Practical Fat-Loss Priorities",
        text: "Focusing on results without extremes or burnout...",
        question: {
          id: "preworkout",
          text: "How is your energy usually when it’s time to work out?",
          answers: ["High, I usually feel ready", "It depends on the day", "Low, I often feel drained"],
        },
        duration: 3500,
      },
      {
        id: "3",
        title: "Designing Efficient Training Sessions",
        text: "Choosing workouts that fit into busy days...",
        question: {
          id: "trainingTime",
          text: "When does your schedule usually allow you to train?",
          answers: ["Morning", "Evening", "It varies"],
        },
        duration: 4000,
      },
      {
        id: "4",
        title: "Balancing Recovery With Work Stress",
        text: "Supporting progress without draining energy...",
        question: {
          id: "sleepHours",
          text: "On most days, how many hours of sleep do you get?",
          answers: ["Less than 6", "6 - 7", "8+"],
        },
        duration: 5000,
      },
      {
        id: "5",
        title: "Making This Work Week After Week",
        text: "Ensuring the plan fits changing schedules and priorities...",
        duration: 4000,
      },
    ];
  }

  if (isSegment3) {
    return [
      {
        id: "1",
        title: "Reviewing Your Health & Movement Readiness",
        text: "Taking mobility, experience, and comfort into account...",
        duration: 5000,
      },
      {
        id: "2",
        title: "Focusing on Healthy, Sustainable Fat Loss",
        text: "Prioritizing steady progress and recovery...",
        question: {
          id: "preworkout",
          text: "How is your energy usually when it’s time to work out?",
          answers: ["High, I usually feel ready", "It depends on the day", "Low, I often feel drained"],
        },
        duration: 3500,
      },
      {
        id: "3",
        title: "Choosing Comfortable Training Times",
        text: "Planning workouts around when your body feels best...",
        question: {
          id: "trainingTime",
          text: "When do you feel most comfortable training?",
          answers: ["Morning", "Evening", "It varies"],
        },
        duration: 4000,
      },
      {
        id: "4",
        title: "Prioritizing Recovery & Joint Health",
        text: "Balancing effort with rest for long-term results...",
        question: {
          id: "sleepHours",
          text: "On most days, how many hours of sleep do you get?",
          answers: ["Less than 6", "6 - 7", "8+"],
        },
        duration: 5000,
      },
      {
        id: "5",
        title: "Designing a Program With Longevity in Mind",
        text: "Structuring training and habits to support long-term health and consistency...",
        duration: 4000,
      },
    ];
  }

  return [
    {
      id: "1",
      title: "Fitness Profile Match",
      text: "Analyzing your workout history and experience level...",
      duration: 5000,
    },
    {
      id: "2",
      title: "Goal Alignment",
      text: "Mapping your primary goals to the most effective training approach...",
      question: {
        id: "preworkout",
        text: "Do you usually take a pre-workout supplement before training?",
        answers: ["Yes, almost every workout", "Occasionally / Never"],
      },
      duration: 3500,
    },
    {
      id: "3",
      title: "Training Plan Fit",
      text: "Selecting exercises and intensity tailored to your current abilities...",
      question: {
        id: "trainingTime",
        text: "Do you prefer training in the morning or evening?",
        answers: ["Morning", "Evening"],
      },
      duration: 4000,
    },
    {
      id: "4",
      title: "Nutrition & Recovery Setup",
      text: "Optimizing your nutrition and recovery approach for your goals...",
      question: {
        id: "sleepHours",
        text: "On most days, how many hours of sleep do you get?",
        answers: ["Less than 6", "6 - 7", "8+"],
      },
      duration: 5000,
    },
    {
      id: "5",
      title: "Lifestyle Integration",
      text: "Adjusting your plan to fit your schedule and daily habits...",
      duration: 4000,
    },
  ];
}

// Dev Helper Utility - can be used in browser console
const DISABLE_LOADING_ON_LANDING_PAGE_KEY = "disable_landing_loading";

export const devHelper = {
  /**
   * Check if loading on landing page is disabled
   */
  isLoadingOnLandingPageDisabled(): boolean {
    return localStorage.getItem(DISABLE_LOADING_ON_LANDING_PAGE_KEY) === "true";
  },

  /**
   * Toggle loading on landing page
   */
  toggleLoadingOnLandingPage(): void {
    const currentValue = this.isLoadingOnLandingPageDisabled();
    localStorage.setItem(DISABLE_LOADING_ON_LANDING_PAGE_KEY, !currentValue ? "true" : "false");
    console.log(`Loading on landing page ${!currentValue ? "disabled" : "enabled"}`);
  },

  getStableID(): string {
    return Cookies.get("bws_statsig_stable_id") || "";
  },
};

// Attach to window object for console access
if (typeof window !== "undefined") {
  (window as any).devHelper = devHelper;
}
